import { useCallback, useEffect, useRef, useState } from 'react';
import { API, authFetch, getAuth } from '../api';

/**
 * Loads /models/output/overview on mount and on refresh().
 *
 * The 5-metric snapshot that powers the Dashboard hero cards and the
 * Insights summary numbers. Lighter than the full ModelOutput payload —
 * just the fields these two screens actually paint.
 *
 * `data.hasOutput=false` means there is no usable run yet; UI should
 * switch to a "no data yet" placeholder rather than rendering 0s.
 * `updatedAt` lets the UI show how stale the cached numbers are.
 *
 * Returns { data, loading, error, refresh }. data is null until the
 * first request resolves.
 */
export function useOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const auth = getAuth();
    if (!auth) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await authFetch(API.modelOverview(), { method: 'GET' });

        if (cancelled || !mountedRef.current) return;

        if (!res.ok) {
          setError(`Failed to load overview (${res.status})`);
          setLoading(false);
          return;
        }

        const json = await res.json();
        if (cancelled || !mountedRef.current) return;

        setData(json);
        setLoading(false);
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        setError(err instanceof Error ? err.message : 'Network error');
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [tick]);

  return { data, loading, error, refresh };
}
